import { EnvelopeIcon, GlobeIcon, MapPinIcon, PhoneIcon } from "@phosphor-icons/react";

import { cn } from "@/utils/style";

import type { TemplateProps } from "./types";

import { getSectionComponent } from "../shared/get-section-component";
import { PageIcon } from "../shared/page-icon";
import { PageLink } from "../shared/page-link";
import { PagePicture } from "../shared/page-picture";
import { useResumeStore } from "../store/resume";

const sectionClassName = cn(
  // Section Layout
  "grid grid-cols-5 border-t border-(--page-primary-color) pt-1",

  // Section Content
  "[&>.section-content]:col-span-4",
);

/**
 * Template: Bronzor
 */
export function BronzorTemplate({ pageIndex, pageLayout }: TemplateProps) {
  const isFirstPage = pageIndex === 0;
  const { main, sidebar, fullWidth } = pageLayout;

  return (
    <div className="template-bronzor page-content space-y-(--page-gap-y) px-(--page-margin-x) pt-(--page-margin-y) print:p-0">
      {isFirstPage && <Header />}

      <div className="space-y-(--page-gap-y)">
        <main data-layout="main" className="group page-main space-y-(--page-gap-y)">
          {main.map((section) => {
            const Component = getSectionComponent(section, { sectionClassName });
            return <Component key={section} id={section} />;
          })}
        </main>

        {!fullWidth && (
          <aside data-layout="sidebar" className="group page-sidebar space-y-(--page-gap-y)">
            {sidebar.map((section) => {
              const Component = getSectionComponent(section, { sectionClassName });
              return <Component key={section} id={section} />;
            })}
          </aside>
        )}
      </div>
    </div>
  );
}

function Header() {
  const basics = useResumeStore((state) => state.resume.data.basics);

  return (
    <div className="page-header flex flex-col items-center gap-y-2">
      <PagePicture />

      <div className="page-basics space-y-2 text-center">
        <div className="basics-header">
          <h2 className="basics-name">{basics.name}</h2>
          <p className="basics-headline">{basics.headline}</p>
        </div>

        <div className="basics-items flex flex-wrap justify-center gap-x-3 gap-y-1 text-center *:flex *:items-center *:gap-x-1.5">
          {basics.email && (
            <div className="basics-item-email">
              <EnvelopeIcon />
              <PageLink url={`mailto:${basics.email}`} label={basics.email} />
            </div>
          )}

          {basics.phone && (
            <div className="basics-item-phone">
              <PhoneIcon />
              <PageLink url={`tel:${basics.phone}`} label={basics.phone} />
            </div>
          )}

          {basics.location && (
            <div className="basics-item-location">
              <MapPinIcon />
              <span>{basics.location}</span>
            </div>
          )}

          {basics.website.url && (
            <div className="basics-item-website">
              <GlobeIcon />
              <PageLink {...basics.website} />
            </div>
          )}

          {basics.customFields.map((field) => (
            <div key={field.id} className="basics-item-custom">
              <PageIcon icon={field.icon} />
              {field.link ? <PageLink url={field.link} label={field.text} /> : <span>{field.text}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
